import { faCheck, faCheckCircle } from '@fortawesome/free-solid-svg-icons';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import styles from '../page.module.css';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

type GuestProfilesProps = {
	profiles?: any[];
	onVerifyGuest: (profile: any) => void;
	verifyingProfiles: Set<number>;
};

const GuestProfiles: React.FC<GuestProfilesProps> = ({
	profiles = [],
	onVerifyGuest,
	verifyingProfiles,
}) => {
	const { t } = useTranslation();
	const handleVerifyGuest = async (profile: any) => {
		if (profile.isPrimary) {
			const confirmMsg =
				t('LandingPageConfirmVerifyPrimary') ||
				'Are you sure you want to proceed as primary guest?';

			const result = await Swal.fire({
				title: t('Confirm') || 'Confirm',
				text: confirmMsg,
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: t('Yes') || 'Yes',
				cancelButtonText: t('No') || 'No',
			});

			if (!result.isConfirmed) return;
		}

		onVerifyGuest(profile);
	};
	const toTitleCase = (text?: string | null) => {
		if (!text) return '';

		const words = text.trim().split(/\s+/).filter(Boolean);
		if (words.length === 0) return '';

		return words
			.map((word) => {
				const lower = word.toLowerCase();
				return lower[0].toUpperCase() + lower.slice(1);
			})
			.join(' ');
	};

	console.log('Profiles:', profiles);
	const isPrimaryVerified = profiles.some((p: any) => {
		const status = p.verificationStatus?.toLowerCase();
		return p.isPrimary && (status === 'verified' || status === 'completed');
	});
	const hasGuestForPrecheckin = profiles.some(
		(p: any) => p.isGuestForPrecheckin === true
	);
	console.log('Is Primary Verified:', isPrimaryVerified);
	let childCount = 0;
	let adultCount = 0;
	return (
		<>
			{profiles.map((profile: any, index: number) => {
				const isVerified =
					profile.verificationStatus?.toLowerCase() === 'completed' ||
					profile.verificationStatus?.toLowerCase() === 'verified';
				const isButtonDisabled = hasGuestForPrecheckin
					? (profile.isGuestForPrecheckin ?? false) !== true
					: !profile.isPrimary && !isPrimaryVerified;

				const key =
					profile.profileID && profile.profileID > 0
						? profile.profileID
						: `temp-${profile.profileID}-${index}`;

				const fullName = `${profile.firstName ?? ''} ${
					profile.lastName ?? ''
				}`.trim();

				// Count logic
				let label = '';
				if (profile.isChild) {
					childCount++;
					label = `Child ${childCount}`;
				} else if (!profile.isPrimary) {
					adultCount++;
					label = `Guest ${adultCount}`;
				}

				return (
					<div key={key} className="mb-3">
						{/* Guest Type */}
						<div className="fw-semibold">
							{profile.isPrimary
								? t('LandingPageVerifyPrimaryGuest')
								: profile.isChild
								? 'Child Profile'
								: t('LandingPageVerifyAccompanyingGuest')}
						</div>
						{/* Guest Card */}
						{/* keep button fixed on right; allow name to shrink/ellipsis rather
						than wrapping */}
						<div
							className="border border-dark rounded p-2 d-flex justify-content-between align-items-center flex-nowrap"
							style={{
								minHeight: '50px',
								backgroundColor: 'var(--opaque-color)',
							}}>
							<div
								className="d-flex align-items-center gap-1 flex-grow-1"
								style={{ minWidth: 0 }}>
								<img
									src={process.env.PUBLIC_URL + '/guest_icon.png'}
									alt="User"
									style={{
										width: '20px',
										height: '20px',
										objectFit: 'contain',
										flexShrink: 0,
									}}
								/>
								{/* the name container is allowed to shrink; text will ellipsis */}
								<span
									className="text-muted text-truncate mb-0"
									style={{
										display: 'inline-block',
										lineHeight: '1.2',
										flexGrow: 1,
										minWidth: 0,
									}}>
									{toTitleCase(
										fullName ||
											(profile.isPrimary ? 'Primary Guest' : label || 'Guest')
									)}
								</span>
							</div>

							{isVerified ? (
								<div className="text-success fw-semibold align-items-center text-center flex-shrink-0 text-nowrap ms-2">
									<FontAwesomeIcon icon={faCheckCircle} /> &nbsp;
									{/* {t('LandingPageVerifiedGuest')} */}
								</div>
							) : (
								<button
									className={`${styles.addGuestBtn} flex-shrink-0 text-nowrap ms-2`}
									onClick={() => handleVerifyGuest(profile)}
									disabled={
										isButtonDisabled || verifyingProfiles.has(profile.profileID)
									}
									title={
										isButtonDisabled ? t('LandingPageVerifyPrimaryFirst') : ''
									}>
									{verifyingProfiles.has(profile.profileID) ? (
										<span
											className="spinner-border spinner-border-sm"
											role="status"
											aria-hidden="true"></span>
									) : profile.verificationStatus === 'Pending' ? (
										t('LandingPageResume')
									) : profile.verificationStatus === 'Shared' ? (
										t('LandingPageShared')
									) : (
										t('LandingPageVerifyGuest')
									)}
								</button>
							)}
						</div>
					</div>
				);
			})}
		</>
	);
};

export default GuestProfiles;
